import { LevelOfProtectionType } from "../../../legalEntity/addLegalEntityDialog/AddLegalEntityDialog.types";
import { useTranslation } from "@localization/useTranslation";
import { ControllerProcess, SharingTypeEnum } from "../../processingActivityOverview/ProcessingActivityOverview.types";
import { ISharingsBaseTableProps } from "./SharingsTable";
import { BaseSharingRowsModel } from "./SharingsTable.types";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { IdNamePair } from "../../../customLists/customListView/CustomListView.types.ts";

export function useSharingFilterData() {
    const { translateString } = useTranslation();

    const sharingTypeNames: Record<number, string> = {
        [SharingTypeEnum.dataProcessor]: translateString("dataProcessors"),
        [SharingTypeEnum.dataController]: translateString("dataControllers"),
        [SharingTypeEnum.disclosure]: translateString("disclosures"),
        [SharingTypeEnum.source]: translateString("sources"),
        [SharingTypeEnum.jointDataController]: translateString("jointDataControllers"),
    };

    // Every group GetLevelOfProtectionGroup can return, so a recipient in a data privacy framework country is
    // filterable rather than silently absent from the picker.
    const levelOfProtectionNames: Record<number, string> = {
        [LevelOfProtectionType.EU]: translateString("eu"),
        [LevelOfProtectionType.EEA]: translateString("eea"),
        [LevelOfProtectionType.ThirdCountry]: translateString("unsafeThirdCountry"),
        [LevelOfProtectionType.AdequateProtection]: translateString("adequiteCountry"),
        [LevelOfProtectionType.DataPrivacyFramework]: translateString("dataPrivacyFramework"),
    };

    return {
        getSharingTypeFilterData: (sharingTypes: Array<SharingTypeEnum>) => getPresentValues(sharingTypes, sharingTypeNames),
        getLevelOfProtectionFilterData: (levelsOfProtection: Array<LevelOfProtectionType>) => getPresentValues(levelsOfProtection, levelOfProtectionNames),
    };
}

// Both filters offer only the values the sharings actually hold, so the user is never given an option that
// empties the table.
function getPresentValues(values: Array<number>, names: Record<number, string>): Array<IdNamePair> {
    return Object.entries(names)
        .filter(([value]) => values.includes(Number(value)))
        .map(([value, name]) => ({ id: value, name }));
}

export function useSharingsBaseTableHook(props: ISharingsBaseTableProps) {
    const { translateString } = useTranslation();
    const { getSharingTypeFilterData, getLevelOfProtectionFilterData } = useSharingFilterData();

    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(props.sessionStorageKey);

    const allRows = props.data?.sharingRows ?? [];
    const hasSharings = props.isLoading || allRows.length > 0;

    const sharingsDataRows = applyClientFilters(
        allRows,
        [
            { id: "sharingType", candidate: (row) => getSharingType(row).toString() },
            { id: "senders", candidate: (row) => row.senderId },
            { id: "recipients", candidate: (row) => row.recipientId },
            { id: "dataCategories", candidate: (row) => row.dataCategories.map((dataCategory) => dataCategory.id) },
            { id: "levelOfProtection", candidate: (row) => row.recipientLevelOfProtection.toString() },
        ],
        selectFilters,
        {},
        filterOperators
    )?.map((row) => {
        return {
            ...row,
            senderRole: translateString(ControllerProcess[Number(row.senderRole)]),
            recipientRole: translateString(ControllerProcess[Number(row.recipientRole)]),
            dataCategoriesString: row.dataCategories.map((dataCategory) => dataCategory.name).join(", "),
        };
    });

    function getSharingType(row: BaseSharingRowsModel) {
        if (row.sharingOrigin.dataProcessorId || row.sharingOrigin.systemId) {
            return SharingTypeEnum.dataProcessor;
        }
        if (row.sharingOrigin.dataControllerId) {
            return SharingTypeEnum.dataController;
        }
        if (row.sharingOrigin.transferId) {
            return SharingTypeEnum.disclosure;
        }
        if (row.sharingOrigin.sourceId) {
            return SharingTypeEnum.source;
        }

        return SharingTypeEnum.jointDataController;
    }

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: "sharingType",
                    label: translateString("sharingType"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.sharingType ?? [],
                    fixedData: getSharingTypeFilterData(allRows.map(getSharingType)),
                },
                {
                    id: "senders",
                    label: translateString("Senders"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.senders ?? [],
                    fixedData: props.data?.senders ?? [],
                },
                {
                    id: "dataCategories",
                    label: translateString("dataCategoryHeader"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.dataCategories ?? [],
                    fixedData: props.data?.dataCategories ?? [],
                },
                {
                    id: "recipients",
                    label: translateString("Recipients"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.recipients ?? [],
                    fixedData: props.data?.recipients ?? [],
                },
                {
                    id: "levelOfProtection",
                    label: translateString("sharingsTo"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.levelOfProtection ?? [],
                    fixedData: getLevelOfProtectionFilterData(allRows.map((row) => row.recipientLevelOfProtection)),
                },
            ],
            filterOperators
        );
    }

    const hasActiveFilters = Object.values(selectFilters).some((v) => v.length > 0);

    return {
        sharingsDataRows,
        hasSharings,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
    };
}
